import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trash2, RefreshCw } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DELETE_CONFIGS = {
  all: { url: "/data/all", label: "ALL data (COA + TB + vouchers + closings)" },
  vouchers: { url: "/data/vouchers", label: "only Vouchers (keeps COA & TB)" },
  tb: { url: "/data/trial-balance", label: "Trial Balance" },
  coa: { url: "/data/coa", label: "only Chart of Accounts" },
};

const DELETE_BUTTONS = [
  { key: "vouchers", label: "Delete Vouchers", tone: "amber" },
  { key: "tb", label: "Delete TB", tone: "amber" },
  { key: "coa", label: "Delete COA", tone: "amber" },
  { key: "all", label: "Delete ALL", tone: "red" },
];

function toneClass(tone) {
  return tone === "red"
    ? "text-red-600 border-red-200 hover:bg-red-50"
    : "text-amber-700 border-amber-200 hover:bg-amber-50";
}

/**
 * Delete / reprocess actions shown below the dashboard stats when data exists.
 */
export default function DataManagementCard({ companyId, onRefresh }) {
  const [deleting, setDeleting] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [tbPeriods, setTbPeriods] = useState([]);
  const [tbHasLegacy, setTbHasLegacy] = useState(false);
  const [tbPeriodToDelete, setTbPeriodToDelete] = useState("__all__");

  // Fetch available TB periods whenever the TB delete is opened
  useEffect(() => {
    if (pendingDelete !== "tb") return;
    let alive = true;
    axios.get(`${API}/trial-balance/periods`, { params: { company_id: companyId } })
      .then((res) => {
        if (!alive) return;
        setTbPeriods(res.data.periods || []);
        setTbHasLegacy(!!res.data.has_legacy_untagged);
        setTbPeriodToDelete("__all__");
      })
      .catch(() => { if (alive) setTbPeriods([]); });
    return () => { alive = false; };
  }, [pendingDelete, companyId]);

  const handleReprocess = async () => {
    setReprocessing(true);
    try {
      const res = await axios.post(`${API}/data/reprocess-vouchers`, null, {
        params: { company_id: companyId },
      });
      toast.success(res.data.message);
      onRefresh?.();
    } catch (err) {
      logger.error("reprocess failed:", err);
      toast.error("Failed to reprocess");
    } finally {
      setReprocessing(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    const cfg = DELETE_CONFIGS[pendingDelete];
    setDeleting(true);
    try {
      const params = { company_id: companyId };
      if (pendingDelete === "tb" && tbPeriodToDelete && tbPeriodToDelete !== "__all__") {
        params.period_month = tbPeriodToDelete;
      }
      const res = await axios.delete(`${API}${cfg.url}`, { params });
      const periodNote =
        pendingDelete === "tb" && tbPeriodToDelete !== "__all__"
          ? ` for ${tbPeriodToDelete}`
          : "";
      const n = res?.data?.deleted?.trial_balance;
      toast.success(`Deleted ${cfg.label}${periodNote}${typeof n === "number" ? ` (${n} rows)` : ""}`);
      setPendingDelete(null);
      onRefresh?.();
    } catch (err) {
      logger.error("delete failed:", err);
      toast.error("Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card className="border border-slate-200 shadow-none">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-sm font-medium text-slate-700">Data Management</p>
            <p className="text-xs text-slate-500 mt-0.5">
              Reprocess vouchers or delete data selectively
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReprocess}
              disabled={reprocessing}
              data-testid="reprocess-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${reprocessing ? "animate-spin" : ""}`} />
              {reprocessing ? "Reprocessing..." : "Reprocess Vouchers"}
            </Button>
            {!pendingDelete ? (
              DELETE_BUTTONS.map((btn) => (
                <Button
                  key={btn.key}
                  variant="outline"
                  size="sm"
                  onClick={() => setPendingDelete(btn.key)}
                  className={toneClass(btn.tone)}
                  data-testid={`delete-${btn.key}-btn`}
                >
                  <Trash2 className="w-4 h-4 mr-1" /> {btn.label}
                </Button>
              ))
            ) : (
              <div className="flex gap-2 items-center bg-red-50 border border-red-200 px-3 py-1.5 rounded-md flex-wrap">
                <span className="text-xs font-medium text-red-800">
                  Delete {DELETE_CONFIGS[pendingDelete].label}?
                </span>
                {pendingDelete === "tb" && (tbPeriods.length > 0 || tbHasLegacy) && (
                  <select
                    value={tbPeriodToDelete}
                    onChange={(e) => setTbPeriodToDelete(e.target.value)}
                    className="text-xs border border-red-300 rounded px-2 py-1 bg-white"
                    data-testid="delete-tb-period-select"
                  >
                    <option value="__all__">All snapshots</option>
                    {tbPeriods.map((p) => (
                      <option key={p} value={p}>Only {p}</option>
                    ))}
                  </select>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPendingDelete(null)}
                  data-testid="delete-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="delete-confirm-btn"
                >
                  {deleting ? "Deleting..." : "Confirm"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
